import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import * as XLSX from "xlsx";
import { useToast } from "@/hooks/use-toast";
import { AppShell } from "@/components/layout/app-shell";
import { resolveUrl } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from "@/components/ui/table";
import {
  Plus,
  Trash2,
  FileSpreadsheet,
  Download,
  Upload,
  Database,
  Search,
  ArrowLeft,
  Loader2,
  Check,
  Info
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const CRITICAL_COLUMNS = [
  { key: "zoneName", label: "Zone Name" },
  { key: "floorRdfsLabel", label: "Floor" },
  { key: "locationRdfsLabel", label: "Location" },
  { key: "spacetype", label: "Space Type" },
  { key: "subclassType", label: "Subclass Type" },
  { key: "fixtureQuantity", label: "Quantity" },
  { key: "existingFixtureWattage", label: "Wattage" },
  { key: "lampType", label: "Lamp Type" },
  { key: "numberOfLampFixtures", label: "Lamps/Fixture" },
  { key: "wattsPerLamp", label: "Wattage/Lamp" },
  { key: "operatingHourPerYear", label: "Hours/Year" },
  { key: "rdfsLabel", label: "Fixture Description" }
];

export default function OsemLightingPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [gridData, setGridData] = useState<any[]>([]);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; colKey: string } | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  const { data: proposal, isLoading } = useQuery<any>({
    queryKey: [`/api/osem/${id}`],
    queryFn: async () => {
      const res = await fetch(resolveUrl(`/api/osem/${id}`));
      if (!res.ok) throw new Error("Failed to load proposal");
      return res.json();
    }
  });

  const { data: lightingState, isLoading: isLoadingLighting } = useQuery<any>({
    queryKey: [`/api/osem/${id}/lighting`],
    queryFn: async () => {
      const res = await fetch(resolveUrl(`/api/osem/${id}/lighting`));
      if (!res.ok) throw new Error("Failed to load lighting data");
      return res.json();
    }
  });

  useEffect(() => {
    if (lightingState) {
      setGridData(lightingState.lightingEntries || []);
      setHasTemplate(lightingState.hasLightingTemplate || false);
    }
  }, [lightingState]);

  const saveMutation = useMutation({
    mutationFn: async (updatedEntries: any[]) => {
      const res = await fetch(resolveUrl(`/api/osem/${id}/lighting`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lightingEntries: updatedEntries })
      });
      if (!res.ok) throw new Error("Failed to save changes");
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Changes Saved",
        description: "Lighting audit spreadsheet changes saved successfully."
      });
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${id}/lighting`] });
    },
    onError: (error: any) => {
      toast({
        title: "Save Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (payload: { fileContent: string; lightingEntries: any[] }) => {
      const res = await fetch(resolveUrl(`/api/osem/${id}/lighting/upload`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Failed to upload template workbook");
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Spreadsheet Uploaded",
        description: `Loaded ${data.lightingEntries?.length || 0} lighting zone rows from workbook.`
      });
      setGridData(data.lightingEntries || []);
      setHasTemplate(true);
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${id}/lighting`] });
    },
    onError: (error: any) => {
      toast({
        title: "Upload Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const seapSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(resolveUrl(`/api/osem/${id}/lighting/seap-sync`), { method: "POST" });
      if (!res.ok) throw new Error("Failed to sync to SEAP");
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "SEAP API Synchronized",
        description: `Successfully synced ${data.syncedRecordsCount} audit records to SEAP. Transaction ID: ${data.syncTransactionId}`
      });
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleUploadWorkbook = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const arrayBuffer = evt.target?.result as ArrayBuffer;
        
        // Convert arrayBuffer to Base64
        const uint8 = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < uint8.length; i++) {
          binary += String.fromCharCode(uint8[i]);
        }
        const base64 = btoa(binary);

        const wb = XLSX.read(arrayBuffer, { type: "array" });
        const ws = wb.Sheets["CSV_BP_Sort"];
        if (!ws) {
          toast({
            title: "Invalid Workbook Format",
            description: "Could not find 'CSV_BP_Sort' worksheet in the uploaded file.",
            variant: "destructive"
          });
          return;
        }

        const parsedData = XLSX.utils.sheet_to_json(ws, { defval: "" });
        uploadMutation.mutate({ fileContent: base64, lightingEntries: parsedData });
      } catch (err: any) {
        toast({
          title: "File Parse Error",
          description: err.message,
          variant: "destructive"
        });
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleCellValueChange = (rowIndex: number, colKey: string, value: any) => {
    const updated = [...gridData];
    updated[rowIndex] = { ...updated[rowIndex], [colKey]: value };
    setGridData(updated);
  };

  const handleAddRow = () => {
    const newRow = {
      zoneName: "New Zone",
      floorRdfsLabel: "1st Floor",
      locationRdfsLabel: "Office Area",
      spacetype: "Office",
      subclassType: "OverheadLEDTube",
      fixtureQuantity: 10,
      existingFixtureWattage: 32,
      lampType: "T8 Fluorescent",
      numberOfLampFixtures: 2,
      wattsPerLamp: 16,
      operatingHourPerYear: 3000,
      rdfsLabel: "Retrofit Lighting Fixture",
      equipmentType: "Lighting"
    };
    setGridData([newRow, ...gridData]);
    setCurrentPage(1);
    toast({ description: "Added new row to audit database." });
  };

  const handleDeleteRow = (index: number) => {
    const updated = [...gridData];
    updated.splice(index, 1);
    setGridData(updated);
    toast({ description: "Deleted selected audit row." });
  };

  // Filters rows based on search term
  const filteredData = gridData.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (row.zoneName?.toLowerCase() || "").includes(term) ||
      (row.floorRdfsLabel?.toLowerCase() || "").includes(term) ||
      (row.locationRdfsLabel?.toLowerCase() || "").includes(term) ||
      (row.rdfsLabel?.toLowerCase() || "").includes(term)
    );
  });

  // Pagination variables
  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const isSaving = saveMutation.isPending;
  const isUploading = uploadMutation.isPending;

  if (isLoading || isLoadingLighting) {
    return (
      <AppShell fluid>
        <div className="flex h-[80vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell fluid>
      <div className="space-y-6 pb-12">
        {/* Top Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b pb-4">
          <div className="flex items-start gap-4">
            <Button
              variant="outline"
              size="icon"
              className="mt-1"
              onClick={() => setLocation(`/osem-onboarding/${id}`)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Russo Lighting Auditor & SEAP Integrator
              </h1>
              <p className="text-sm text-muted-foreground">
                Interactive auditing spreadsheet for {proposal?.companyName || "Facility"}.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(`/api/osem/${id}/lighting/export-csv`, "_blank")}
              disabled={gridData.length === 0}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export SEAP CSV
            </Button>

            <Button
              variant="outline"
              onClick={() => window.open(`/api/osem/${id}/lighting/download`, "_blank")}
              disabled={false}
            >
              <Download className="h-4 w-4 mr-2" /> Download Excel (.xlsx)
            </Button>

            <Button
              variant="secondary"
              onClick={() => seapSyncMutation.mutate()}
              disabled={gridData.length === 0 || seapSyncMutation.isPending}
            >
              {seapSyncMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              Sync to SEAP API
            </Button>

            <Button
              onClick={() => saveMutation.mutate(gridData)}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Audit Upload & Status Info */}
        {!hasTemplate ? (
          <Card className="border border-dashed border-primary/40 bg-primary/5">
            <CardContent className="flex flex-col items-center justify-center py-10 space-y-4">
              <Upload className="h-10 w-10 text-primary animate-pulse" />
              <div className="text-center space-y-1">
                <CardTitle className="text-lg">Upload Lighting Audit Workbook</CardTitle>
                <CardDescription className="text-sm">
                  Upload the official "250214 Appendix I Russo Lighting.xlsx" to populate the auditor database.
                </CardDescription>
              </div>
              <div className="flex flex-col items-center gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleUploadWorkbook}
                    disabled={isUploading}
                  />
                  <div className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 text-sm font-medium h-9 shadow">
                    {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Choose Excel File
                  </div>
                </label>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`/api/osem/${id}/lighting/download`, "_blank")}
                >
                  <Download className="h-3.5 w-3.5 mr-2" /> Download Blank Template Workbook
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Alert className="bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400">
            <Info className="h-4 w-4" />
            <AlertTitle className="text-sm font-semibold">Workbook Template Linked</AlertTitle>
            <AlertDescription className="text-xs">
              Lighting spreadsheet template loaded. Changes will automatically preserve the 32 secondary calc/weather tabs when downloading the workbook.
            </AlertDescription>
          </Alert>
        )}

        {/* Spreadsheet Capability Grid */}
        <Card className="shadow-none border">
          <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between pb-4 space-y-2 md:space-y-0">
            <div>
              <CardTitle className="text-base font-semibold">Lighting Audit Database</CardTitle>
              <CardDescription className="text-xs">
                Inline edit cell properties. Double click cell to edit values.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search zone, location, desc..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-8 h-9 text-xs"
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleAddRow} className="h-9">
                <Plus className="h-4 w-4 mr-1" /> Add Row
              </Button>
              {hasTemplate && (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleUploadWorkbook}
                  />
                  <div className="inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-xs font-medium h-9 px-3">
                    <Upload className="h-4 w-4 mr-1" /> Re-upload Excel
                  </div>
                </label>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {gridData.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground italic border-t">
                No lighting data found. Please upload a Russo Lighting audit workbook to populate the spreadsheet.
              </div>
            ) : (
              <div className="overflow-x-auto border-t">
                <Table className="border-collapse table-fixed min-w-[1500px]">
                  <TableHeader className="bg-muted/40">
                    <TableRow className="h-8 hover:bg-transparent">
                      <TableHead className="w-10 text-center px-1 font-bold text-xs uppercase border-r">#</TableHead>
                      {CRITICAL_COLUMNS.map((col) => (
                        <TableHead key={col.key} className="px-2 font-bold text-xs uppercase border-r text-foreground">
                          {col.label}
                        </TableHead>
                      ))}
                      <TableHead className="w-14 text-center px-1 font-bold text-xs uppercase text-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  
                  <TableBody className="divide-y">
                    {paginatedData.map((row, relativeIdx) => {
                      const absoluteIdx = (currentPage - 1) * rowsPerPage + relativeIdx;
                      return (
                        <TableRow key={absoluteIdx} className="h-9 hover:bg-muted/30">
                          <TableCell className="text-center text-xs font-semibold border-r bg-muted/20 text-muted-foreground py-1 px-1">
                            {absoluteIdx + 1}
                          </TableCell>
                          
                          {CRITICAL_COLUMNS.map((col) => {
                            const isEditing = editingCell?.rowIndex === absoluteIdx && editingCell?.colKey === col.key;
                            return (
                              <TableCell
                                key={col.key}
                                onDoubleClick={() => setEditingCell({ rowIndex: absoluteIdx, colKey: col.key })}
                                className="p-0 text-xs border-r overflow-hidden truncate align-middle h-9 relative"
                              >
                                {isEditing ? (
                                  <Input
                                    value={row[col.key] || ""}
                                    className="h-9 text-xs border-none focus-visible:ring-1 focus-visible:ring-primary rounded-none px-2 absolute inset-0 w-full"
                                    onChange={(e) => handleCellValueChange(absoluteIdx, col.key, e.target.value)}
                                    onBlur={() => setEditingCell(null)}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === "Escape") {
                                        setEditingCell(null);
                                      }
                                    }}
                                  />
                                ) : (
                                  <div className="px-2 select-none py-1.5 cursor-pointer truncate max-w-full">
                                    {row[col.key]}
                                  </div>
                                )}
                              </TableCell>
                            );
                          })}
                          
                          <TableCell className="text-center p-0 align-middle">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteRow(absoluteIdx)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {filteredData.length > 0 && (
              <div className="flex items-center justify-between border-t py-4 px-6 bg-muted/10">
                <div className="text-xs text-muted-foreground">
                  Showing {Math.min(filteredData.length, (currentPage - 1) * rowsPerPage + 1)}-
                  {Math.min(filteredData.length, currentPage * rowsPerPage)} of {filteredData.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <div className="text-xs font-semibold px-2">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
